import React, { FC, memo, useMemo } from 'react';
import { Box, Grid, SelectChangeEvent, Typography } from '@mui/material';
import LabelWrapper from '../../common/LabelWrapper';
import CustomInput from '../../custom/CustomInput';
import CustomSwitch from '../../custom/CustomSwitch';
import BoxComponent from '../../custom/BoxComponent';
import CustomCheckbox from '../../custom/CustomCheckbox';
import CustomSelect from '../../custom/CustomSelect';
import CustomMenuItem from '../../custom/CustomMenuItem';
import { ShorelineOutput } from '../../../models/inputTypes/ShorelineFields';

const styles = {
  spacingContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  alignEndContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  childSpacing: {
    '& >:nth-child(n)': {
      my: 1,
      mr: 1,
    },
  },
  childMargin: {
    '& >:nth-child(n)': {
      my: 1,
      mr: 4,
    },
  },
  flexStartContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
} as const;

type InputsProps = {
  inputState: ShorelineOutput;
  setInputState: (value: ShorelineOutput) => void;
};
const ShorelineOutputGroup: FC<InputsProps> = ({ inputState, setInputState }) => {
  const onInputChange = (
    event: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const onCheckboxClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue: 'geojson' | 'csv' | 'bargraph' = e.target.id;
    const newValue = inputState[currentValue] ? 0 : 1;
    setInputState({ ...inputState, [currentValue]: newValue });
  };

  const onNumberChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputState({ ...inputState, [event.target.name]: event.target.value });
  };

  const switchChange = (e: any) => {
    setInputState({ ...inputState, [e.target.name]: e.target.checked });
  };

  return (
    <>
      <Box>
        <LabelWrapper label={'Enter Output filename'}>
          <CustomInput
            type={'text'}
            required
            maxWidth={'200px'}
            placeholder={'Enter name'}
            value={inputState.outputFileName}
            name={'outputFileName'}
            onChange={onInputChange}
          />
        </LabelWrapper>
      </Box>
      <Box mt={2}>
        <LabelWrapper label={'File Type'}>
          <Box sx={{ ...styles.alignEndContainer }}>
            <Box sx={{ ...styles.spacingContainer, ...styles.childSpacing }}>
              <CustomCheckbox checked={!!inputState.csv} />
              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                .csv
              </Typography>
            </Box>
            <Box sx={{ ...styles.alignEndContainer }}>
              <Box mr={2} sx={{ ...styles.spacingContainer, ...styles.childSpacing }}>
                <CustomCheckbox checked={!!inputState.geojson} id={'geojson'} onClick={onCheckboxClick} />
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  .geojson
                </Typography>
              </Box>
              <BoxComponent disabled={!inputState.geojson}>
                <LabelWrapper label={'CRS EPSG code'}>
                  <CustomInput
                    required
                    placeholder={'-'}
                    min={1024}
                    max={32767}
                    step={1}
                    maxWidth={'85px'}
                    disabled={!inputState.geojson}
                    errorText={'Enter number 1024-32767'}
                    type={'number'}
                    value={inputState.inpEPSG}
                    name={'inpEPSG'}
                    onChange={onNumberChange}
                  />
                </LabelWrapper>
              </BoxComponent>
            </Box>
            <Box sx={{ ...styles.alignEndContainer }}>
              <Box mr={2} sx={{ ...styles.spacingContainer, ...styles.childSpacing }}>
                <CustomCheckbox checked={!!inputState.bargraph} id={'bargraph'} onClick={onCheckboxClick} />
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  .png Bargraph
                </Typography>
              </Box>
              <BoxComponent mr={2} disabled={!inputState.bargraph}>
                <LabelWrapper label={'scale'}>
                  <CustomInput
                    required
                    maxWidth={'85px'}
                    disabled={!inputState.bargraph}
                    // errorText={'Enter number 1024-32767'}
                    type={'number'}
                    value={inputState.bgscale}
                    name={'bgscale'}
                    onChange={onNumberChange}
                  />
                </LabelWrapper>
              </BoxComponent>
              <BoxComponent disabled={!inputState.bargraph} sx={{ ...styles.flexStartContainer }}>
                <BoxComponent disabled={!inputState.bargraph}>
                  <LabelWrapper label={'Plot Basemap'}>
                    <Grid container justifyContent={'center'} width={'100px'}>
                      <CustomSwitch checked={inputState.plotBasemap} name={'plotBasemap'} onChange={switchChange} />
                    </Grid>
                  </LabelWrapper>
                </BoxComponent>
                <BoxComponent mr={2} disabled={!inputState.bargraph}>
                  <LabelWrapper label={'CRS EPSG code'}>
                    <CustomInput
                      required
                      placeholder={'-'}
                      min={1024}
                      max={32767}
                      step={1}
                      maxWidth={'85px'}
                      disabled={!inputState.plotBasemap}
                      errorText={'Enter number 1024-32767'}
                      type={'number'}
                      value={inputState.inpEPSG}
                      name={'inpEPSG'}
                      onChange={onNumberChange}
                    />
                  </LabelWrapper>
                </BoxComponent>
                <Box>
                  <LabelWrapper label={'Map Layer'}>
                    <CustomSelect
                      required
                      placeholder={'-'}
                      disabled={!inputState.plotBasemap}
                      value={inputState.bgProvider}
                      name={'bgProvider'}
                      onChange={onInputChange}
                      minWidth={'140px'}
                      defaultValue={''}
                      displayEmpty
                    >
                      <CustomMenuItem key={'map-1'} value={1}>
                        <Typography variant={'subtitle2'}>World Satellite (ESRI)</Typography>
                      </CustomMenuItem>
                      <CustomMenuItem key={'map-2'} value={2}>
                        <Typography variant={'subtitle2'}>World Street Map (ESRI)</Typography>
                      </CustomMenuItem>
                      <CustomMenuItem key={'map-3'} value={3}>
                        <Typography variant={'subtitle2'}>World Topo Map (ESRI)</Typography>
                      </CustomMenuItem>
                    </CustomSelect>
                  </LabelWrapper>
                </Box>
              </BoxComponent>
            </Box>
          </Box>
        </LabelWrapper>
      </Box>
    </>
  );
};

export default memo(ShorelineOutputGroup);
